import { Link } from "react-router-dom";
import { ArrowUpRight, Award } from "lucide-react";

const IMG_FACILITY = "https://images.unsplash.com/photo-1606185540834-d6e7483ee1a4";
const IMG_FOUNDER = "https://customer-assets.emergentagent.com/job_chesterton-security/artifacts/yfjgic90_Jack%20Headshot.jpeg";

const certifications = [
  "Licensed Attorney (Bar Admitted, 2010)",
  "ITAR / EAR Export Compliance Counsel",
  "Facility Security Officer (FSO) Program Design",
  "Maritime, Aerospace, Space & Defense Sector Specialist",
  "ASIS Certified Protection Professional (CPP)",
  "ISO 28000 Supply Chain Security",
  "Voluntary Self-Disclosure (VSD) Practice",
  "Tabletop & Live Compliance Training",
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-slate-950">
      {/* HERO / STORY */}
      <section data-testid="about-hero" className="bg-slate-950 border-b border-slate-800 pt-20 pb-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="overline mb-6 anim-up">Our Practice</p>
          <h1 className="anim-up anim-delay-1 font-serif text-5xl sm:text-6xl lg:text-8xl text-slate-50 leading-[0.95] tracking-tight max-w-5xl">
            A <span className="italic text-amber-500">disciplined</span> counsel
            for industries where the regulator is always reading.
          </h1>
        </div>
      </section>

      {/* STORY */}
      <section className="bg-slate-950 py-24 lg:py-32 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] overflow-hidden border border-slate-800">
                <img
                  src={IMG_FACILITY}
                  alt="Port facility"
                  className="w-full h-full object-cover grayscale-[40%]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 to-transparent" />
              </div>
            </div>
            <div className="lg:col-span-7 space-y-10">
              <p className="font-serif text-3xl sm:text-4xl text-slate-50 leading-[1.15]">
                <span className="font-serif text-amber-500 text-7xl float-left leading-[0.7] mr-3 mt-2">C</span>
                hesterton Security exists because compliance — done seriously —
                is not a deliverable. It is a posture. The plans, controls,
                and training we produce are built to be lived with, audited
                against, and defended in front of the people who matter.
              </p>
              <p className="text-slate-300 text-lg leading-relaxed">
                The practice is principal-led by design. Engagements are
                scoped narrowly, staffed thinly, and signed personally. We do
                not pitch you a team of associates you will never meet — the
                attorney who diagnoses your program is the attorney who
                designs it, sits with your operators, and answers when the
                regulator calls.
              </p>
              <p className="text-slate-400 text-lg leading-relaxed">
                Our clients operate in sectors where the regulator is patient
                and the consequences are not — where the reputational scar
                tissue from a single export violation can outlast a
                leadership tenure. The work, accordingly, is quiet,
                methodical, and almost never spoken about in public. We
                prefer it that way.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section data-testid="about-leadership" className="bg-slate-900 py-28 lg:py-40 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-16">
            <p className="overline mb-6">Founder</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-slate-50 leading-[1.05] tracking-tight max-w-3xl">
              Every engagement is signed by the <span className="italic text-amber-500">founder</span>.
            </h2>
          </div>

          <article
            data-testid="founder-card"
            className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-slate-800 border border-slate-800"
          >
            <div className="lg:col-span-5 bg-slate-900">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={IMG_FOUNDER}
                  alt="Jack Shelton, Founding Principal of Chesterton Security"
                  className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                />
              </div>
            </div>
            <div className="lg:col-span-7 bg-slate-900 p-10 lg:p-16 flex flex-col justify-center">
              <p className="overline mb-3">Founding Principal · Attorney</p>
              <h3 className="font-serif text-5xl sm:text-6xl text-slate-50 mb-8 tracking-tight">
                Jack Shelton
              </h3>
              <div className="space-y-6 text-slate-300 text-lg leading-relaxed">
                <p>
                  Jack Shelton has practiced law since 2010, building a
                  focused counsel at the intersection of regulated industry
                  and national security. His work centers on U.S. export
                  compliance and facility security planning across the
                  <span className="text-slate-100"> maritime, aerospace, space, and defense</span> sectors.
                </p>
                <p className="text-slate-400">
                  Jack has stood beside clients of every size — three-person
                  startups standing up a first ITAR program, mid-cap
                  suppliers preparing for a BIS audit, and publicly traded
                  operators reinforcing enterprise controls. The work always
                  extends past the binder: tightening compliance posture,
                  hardening facilities, and training the personnel who have
                  to live with the program every day.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-px bg-slate-800 border border-slate-800 mt-10">
                <div className="bg-slate-900 p-5">
                  <p className="overline mb-2">Practice Since</p>
                  <p className="font-serif text-2xl text-amber-500">2010</p>
                </div>
                <div className="bg-slate-900 p-5">
                  <p className="overline mb-2">Focus</p>
                  <p className="font-serif text-xl text-slate-100 leading-tight">Export Compliance &amp; Facility Security</p>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section data-testid="about-certs" className="bg-slate-950 py-28 lg:py-40 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-4">
              <p className="overline mb-6">Credentials &amp; Capabilities</p>
              <h2 className="font-serif text-4xl sm:text-5xl text-slate-50 leading-[1.05] tracking-tight">
                The <span className="italic text-amber-500">credentials</span> our work rests on.
              </h2>
              <p className="text-slate-400 mt-6 leading-relaxed">
                A focused, attorney-led practice — built around the regimes
                that matter most to maritime, aerospace, space, and defense
                operators.
              </p>
            </div>
            <div className="lg:col-span-8">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-800 border border-slate-800">
                {certifications.map((c) => (
                  <li
                    key={c}
                    className="bg-slate-950 p-6 flex items-start gap-4 hover:bg-slate-900 transition-colors"
                  >
                    <Award className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" strokeWidth={1.2} />
                    <span className="text-slate-200 text-sm leading-relaxed">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <h2 className="lg:col-span-8 font-serif text-4xl sm:text-5xl lg:text-6xl text-slate-50 leading-[1.05] tracking-tight">
              Discreet. Senior. <span className="italic text-amber-500">Accountable.</span>
            </h2>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/contact"
                data-testid="about-cta-button"
                className="inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-500 text-white px-8 py-5 text-xs uppercase tracking-[0.22em] font-medium transition-colors"
              >
                Engage Chesterton
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
